import { absoluteUrl } from "@/lib/site";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { PlaceholderArt } from "@/components/site/PlaceholderArt";
import { SectionHeader } from "@/components/site/SectionHeader";

export const Route = createFileRoute("/visuals")({
  component: Visuals,
  head: () => ({
    meta: [
      { title: "Visuals · Buddha KA · The Visual Transmission" },
      {
        name: "description",
        content:
          "Cover art, portraits, activation art, music visuals, and sacred symbols from the KAfrequency world.",
      },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Visuals · Buddha KA" },
      {
        property: "og:description",
        content: "Cover art, portraits, activation art, and sacred symbols from the KAfrequency world.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/visuals") },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/visuals") }],
  }),
});


const CATEGORIES = ["All", "Portraits", "Cover Art", "Activation Art", "Sacred Symbols", "Video"] as const;

function Visuals() {
  const [cat, setCat] = useState<(typeof CATEGORIES)[number]>("All");
  const items = Array.from({ length: 12 }).map((_, i) => ({
    id: i,
    category: CATEGORIES[(i % (CATEGORIES.length - 1)) + 1],
    aspect: i % 4 === 0 ? ("portrait" as const) : i % 3 === 0 ? ("video" as const) : ("square" as const),
  }));
  const filtered = cat === "All" ? items : items.filter((it) => it.category === cat);

  return (
    <>
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-30" aria-hidden />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Visual Transmission</p>
          <h1 className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl">Visuals</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Cover art, portraits, activation art, music visuals, and sacred symbols from the KAfrequency world.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="mb-10 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`rounded-full border px-4 py-2 text-xs transition-colors ${
                cat === c ? "border-gold bg-gold text-obsidian" : "hairline text-muted-foreground hover:border-gold hover:text-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((it) => (
            <figure key={it.id} className="group relative">
              <PlaceholderArt label={`[${it.category} · Visual ${it.id + 1}]`} aspect={it.aspect} />
              <figcaption className="mt-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {it.category}
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-12 rounded-xl border hairline bg-charcoal p-8 text-center">
          <SectionHeader eyebrow="Phase 2" title="Full lightbox archive." align="center" description="Categories, tags, video loops, and downloadable assets arrive with the full media pipeline." />
          <Link to="/blueprint" className="mt-6 inline-flex rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian">
            View blueprint →
          </Link>
        </div>
      </section>
    </>
  );
}
